import React from 'react';
import { FieldProps, Field, getIn } from 'formik';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import { FormikFieldProps } from 'formik-antd/lib/FieldProps';
import * as Yup from 'yup';

import BaseFileUpload, {
  BaseFileUploadProps,
  FileValue,
} from 'components/wrappers/BaseFileUpload';
import { FileUploadTypeEnum } from 'components/wrappers/BaseFileUpload';
import { getFileByManual } from 'utils/fileUtils';
import { FileParameter } from 'api/backofficeApiClient';

export const BaseFileYupObject = Yup.object({
  fileName: Yup.string(),
  s3Key: Yup.string(),
});

type OwnProps = {
  onChange?: (
    fileValues: FileValue[] | string[] | FileParameter[],
    originalValue: UploadFile[],
    fileInfo: UploadChangeParam,
  ) => void;
};

type InputProps = Omit<BaseFileUploadProps, 'onChange'> &
  FormikFieldProps &
  OwnProps;

const getS3Keys = (
  fileList: UploadFile[],
  includeFileName: boolean = false,
): FileValue[] | string[] => {
  if (includeFileName) {
    return fileList
      .map(
        (file: UploadFile): FileValue => ({
          fileName: file.name,
          s3Key: file.response?.s3Key as string,
        }),
      )
      .filter((file) => file.s3Key);
  }

  return fileList
    .map((file: UploadFile) => file.response?.s3Key)
    .filter(Boolean);
};

export default function FormikFileUpload({
  name,
  validate,
  fast,
  onChange,
  includeFileName,
  typeUploader = FileUploadTypeEnum.Instant,
  ...restProps
}: InputProps) {
  return (
    <Field name={name} validate={validate} fast={fast}>
      {({
        field: { value },
        form: { setFieldValue, setFieldTouched, errors, touched },
      }: FieldProps) => {
        // FormItem copy
        const error = getIn(errors, name, undefined);
        const isTouched = Boolean(getIn(touched, name, false));
        const hasError = error !== undefined && isTouched;

        return (
          <BaseFileUpload
            value={value}
            name={name}
            onChange={(info: UploadChangeParam) => {
              const { fileList } = info;
              const isManual = typeUploader === FileUploadTypeEnum.Manual;

              const s3Keys = getS3Keys(fileList, includeFileName);
              const files = getFileByManual(fileList);
              const correctValue = isManual ? files : s3Keys;

              setFieldValue(name, correctValue); // store only s3Keys/file in the formik
              setFieldTouched(name, true, true);

              onChange && onChange(correctValue, value, info); // pass to the component s3Keys, original value and file info
            }}
            // BaseFileUpload should have access to errors
            // in order to regulate its displaying, so
            // it's not displayed in FormItem component
            error={hasError ? error : undefined}
            handleFieldTouch={() => {
              setTimeout(() => {
                setFieldTouched(name, true, true);
              }, 500);
            }}
            typeUploader={typeUploader}
            {...restProps}
          />
        );
      }}
    </Field>
  );
}
